import axios, { AxiosError } from "axios";
import { plainToInstance } from "class-transformer";
import { GlobalAdminOrganizationProjectDto, GlobalAdminProjectOwnerDto } from "healthex-common/global-admin-projects.dtos";
import { err } from "healthex-common/result";
import { StudyConflictErrorDto, StudyDto, StudyUserDto, StudyVersionStatus, } from "healthex-common/study.dtos";
import { authenticatedApiConfig, generateURL } from "./BaseApiService";
function studyVersionInputToQuery(versionInput) {
    if (versionInput === StudyVersionStatus.DRAFT) {
        return "draft";
    }
    if (versionInput === StudyVersionStatus.ACTIVE) {
        return "active";
    }
    if (versionInput === "ACTIVE_FALLBACK_TO_DRAFT") {
        return "active-fallback-to-draft";
    }
    return "active-fallback-to-draft";
}
export class GlobalAdminProjectsService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async listProjects(organizationId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects", { orgId: organizationId }), await authenticatedApiConfig(this.authenticationProvider));
        if (!Array.isArray(response.data)) {
            return [];
        }
        return response.data.map((item) => plainToInstance(GlobalAdminOrganizationProjectDto, item));
    }
    async listProjectOwners(organizationId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/project-owners", { orgId: organizationId }), await authenticatedApiConfig(this.authenticationProvider));
        if (!Array.isArray(response.data)) {
            return [];
        }
        return response.data.map((item) => plainToInstance(GlobalAdminProjectOwnerDto, item));
    }
    async createProject(organizationId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects", { orgId: organizationId }), input, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getProject(organizationId, projectId, versionInput) {
        var _a;
        try {
            const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId", {
                orgId: organizationId,
                projectId,
            }), await authenticatedApiConfig(this.authenticationProvider, { version: studyVersionInputToQuery(versionInput) }));
            if (response.data === null || response.data === undefined) {
                return null;
            }
            return plainToInstance(StudyDto, response.data);
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 404) {
                return null;
            }
            throw e;
        }
    }
    async updateProjectDraft(organizationId, projectId, update) {
        var _a;
        try {
            const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/draft", {
                orgId: organizationId,
                projectId,
            }), update, await authenticatedApiConfig(this.authenticationProvider));
            return plainToInstance(StudyDto, response.data);
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 409) {
                return err(plainToInstance(StudyConflictErrorDto, e.response.data));
            }
            throw e;
        }
    }
    async getProjectDraftVersionNumber(organizationId, projectId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/draft/version-number", {
            orgId: organizationId,
            projectId,
        }), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async createProjectDraftIfNecessary(organizationId, projectId) {
        await axios.post(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/draft/$create-if-necessary", {
            orgId: organizationId,
            projectId,
        }), {}, await authenticatedApiConfig(this.authenticationProvider));
    }
    async getOrgIconUrl(organizationId, projectId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/draft/org-icon-url", {
            orgId: organizationId,
            projectId,
        }), localFileInfo, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrgRectangularIconUrl(organizationId, projectId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/draft/org-rectangular-icon-url", {
            orgId: organizationId,
            projectId,
        }), localFileInfo, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async deleteProjectDraft(organizationId, projectId) {
        await axios.delete(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/draft", {
            orgId: organizationId,
            projectId,
        }), await authenticatedApiConfig(this.authenticationProvider));
    }
    async listProjectUsers(organizationId, projectId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/users", {
            orgId: organizationId,
            projectId,
        }), await authenticatedApiConfig(this.authenticationProvider));
        if (!Array.isArray(response.data)) {
            return [];
        }
        return plainToInstance(StudyUserDto, response.data);
    }
    async addProjectUser(organizationId, projectId, userId, input) {
        await axios.put(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/users/:userId", {
            orgId: organizationId,
            projectId,
            userId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
    }
    async removeProjectUser(organizationId, projectId, userId) {
        await axios.delete(generateURL(this.environment.API_BASE_URL, "/admin/organizations/:orgId/projects/:projectId/users/:userId", {
            orgId: organizationId,
            projectId,
            userId,
        }), await authenticatedApiConfig(this.authenticationProvider));
    }
}
